import { useMemo } from 'react';

import type { useGetSeedFilters } from '@/hooks/api/useGetSeedFilters';
import type { SeedFilter } from '@/hooks/useSeeds';
import { reconcileRegistrarFilterValues } from '@/utils/formatRegistrar.util';
import { reconcileMergedFilterValues } from '@/utils/mergeFilterOptions.util';

/** The `data` shape returned by {@link useGetSeedFilters}. */
type SeedFilterOptions = ReturnType<typeof useGetSeedFilters>['data'];

/** Runtime option shape the reconcile functions consume (`rawValues` is a
 * runtime-only augmentation of the base FilterOption/GroupOption types). */
type ReconcilableOptions = ReadonlyArray<{
  value?: string | number;
  rawValues?: string[];
}>;

/** Merged filter keys reconciled via the options-driven merged utility.
 * Registrar is reconciled separately with its options-free registrar
 * reconcile (legacy "name [Tag = X]" handling). */
const MERGED_RECONCILE_KEYS = ['registrant', 'customer'] as const;

/**
 * Derives the effective seed filter by reconciling persisted merged-key
 * filters — registrar (legacy "name [Tag = X]" values or a frozen expanded
 * raw set) plus the merged registrant/customer keys (ENG-4329) — against the
 * CURRENT filter options so the IN-query — and the activeQuery used by
 * select-all-matching + export — matches every raw variant of the selected
 * labels. Stored state is intentionally untouched: this hook is DERIVE-ONLY
 * and never writes the reconciled values back to storage.
 *
 * Superset semantics: {@link reconcileRegistrarFilterValues} and
 * {@link reconcileMergedFilterValues} return the UNION of the stored values
 * and their re-expansion, so the effective filter can only ever match MORE
 * rows than the stored filter, never fewer.
 *
 * Fallback while options load — or when no reconcilable key has values — is
 * to pass the stored `filter` through (today's behavior). The early returns
 * deliberately return the SAME `filter` reference (no cloning), so downstream
 * referential-identity checks are unaffected when no reconciliation applies.
 *
 * @param filter - The current seed filter state
 * @param seedFilterOptions - Filter options from {@link useGetSeedFilters}
 * @param filterOptionsStatus - Load status of the filter options query
 * @returns The reconciled filter, or the stored `filter` reference when no
 *   reconciliation applies
 */
export function useEffectiveSeedFilter(
  filter: SeedFilter,
  seedFilterOptions: SeedFilterOptions,
  filterOptionsStatus: 'error' | 'success' | 'pending'
): SeedFilter {
  return useMemo<SeedFilter>(() => {
    if (filterOptionsStatus !== 'success') return filter;

    let reconciled: Record<string, string[]> | null = null;

    const registrar = filter.advancedFilters?.registrar;
    const registrarOptions = seedFilterOptions?.registrar;
    if (registrar && registrar.length > 0 && registrarOptions) {
      reconciled = {
        registrar: reconcileRegistrarFilterValues(
          registrar,
          registrarOptions as ReconcilableOptions
        ),
      };
    }

    for (const key of MERGED_RECONCILE_KEYS) {
      const stored = filter.advancedFilters?.[key];
      const options = seedFilterOptions?.[key];
      if (stored && stored.length > 0 && options) {
        reconciled = {
          ...(reconciled ?? {}),
          [key]: reconcileMergedFilterValues(
            stored,
            options as ReconcilableOptions
          ),
        };
      }
    }

    if (!reconciled) return filter;
    return {
      ...filter,
      advancedFilters: { ...filter.advancedFilters, ...reconciled },
    };
  }, [filter, seedFilterOptions, filterOptionsStatus]);
}
